import React, { useState, useRef, useEffect } from 'react'
import { Bot, X, Send, Loader2, Sparkles } from 'lucide-react'
import { askAssistant, type ChatMessage } from '../../services/AIService'

const SUGGESTIONS = [
  '¿Cuánto vendimos hoy?',
  '¿Cuál es el producto más pedido?',
  '¿Hay stock bajo en inventario?',
  '¿La caja está abierta?',
]

export default function AIAssistant() {
  const [open, setOpen]       = useState(false)
  const [input, setInput]     = useState('')
  const [loading, setLoading] = useState(false)
  const [history, setHistory] = useState<ChatMessage[]>([])
  const bottomRef             = useRef<HTMLDivElement>(null)
  const inputRef              = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 150)
    }
  }, [open])

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [history, loading])

  const send = async (text: string) => {
    const msg = text.trim()
    if (!msg || loading) return

    const userMsg: ChatMessage = { role: 'user', content: msg }
    setHistory(h => [...h, userMsg])
    setInput('')
    setLoading(true)

    try {
      const reply = await askAssistant(msg, history.slice(-6))
      setHistory(h => [...h, { role: 'assistant', content: reply }])
    } catch {
      setHistory(h => [...h, {
        role: 'assistant',
        content: '⚠️ No pude conectarme al asistente. Verifica la conexión.',
      }])
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      {/* Botón flotante */}
      <button
        onClick={() => setOpen(o => !o)}
        className={`ai-assistant-fab fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full shadow-lg transition-all duration-300
          ${open ? 'bg-red-500 hover:bg-red-600 rotate-90' : 'bg-primary hover:bg-primary/90'}
          text-white`}
        title="Asistente IA"
        aria-label="Abrir asistente IA"
      >
        {open ? <X size={22} /> : <Bot size={22} />}
      </button>

      {/* Panel del chat */}
      <div
        className={`fixed bottom-24 right-6 z-50 flex w-80 flex-col rounded-2xl border border-border bg-surface shadow-2xl transition-all duration-300
          ${open ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 translate-y-4 pointer-events-none'}`}
        style={{ maxHeight: '28rem' }}
      >
        {/* Header */}
        <div className="flex items-center gap-3 rounded-t-2xl border-b border-border bg-primary/5 px-4 py-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-white">
            <Sparkles size={15} />
          </div>
          <div>
            <p className="text-sm font-semibold text-text">Asistente DufiWaiter</p>
            <p className="text-xs text-text-muted">Pregúntame sobre el negocio</p>
          </div>
        </div>

        {/* Mensajes */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3" style={{ minHeight: '12rem' }}>
          {history.length === 0 && (
            <div className="space-y-2">
              <p className="text-xs text-text-muted text-center">Sugerencias:</p>
              {SUGGESTIONS.map(s => (
                <button
                  key={s}
                  onClick={() => send(s)}
                  className="w-full rounded-xl border border-border px-3 py-2 text-left text-xs text-text-muted transition hover:border-primary/40 hover:bg-primary/5 hover:text-text"
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          {history.map((msg, i) => (
            <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-[85%] rounded-2xl px-3 py-2 text-xs leading-relaxed
                  ${msg.role === 'user'
                    ? 'rounded-br-sm bg-primary text-white'
                    : 'rounded-bl-sm bg-muted text-text border border-border'
                  }`}
              >
                {msg.content}
              </div>
            </div>
          ))}

          {loading && (
            <div className="flex justify-start">
              <div className="flex items-center gap-1.5 rounded-2xl rounded-bl-sm bg-muted px-3 py-2 text-xs text-text-muted border border-border">
                <Loader2 size={11} className="animate-spin" />
                Pensando…
              </div>
            </div>
          )}

          <div ref={bottomRef} />
        </div>

        {/* Input */}
        <div className="border-t border-border px-3 py-3">
          <form
            onSubmit={e => { e.preventDefault(); send(input) }}
            className="flex items-center gap-2"
          >
            <input
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder="Pregunta algo…"
              disabled={loading}
              className="flex-1 rounded-xl border border-border bg-bg px-3 py-2 text-xs text-text placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
            />
            <button
              type="submit"
              disabled={!input.trim() || loading}
              className="flex h-8 w-8 items-center justify-center rounded-xl bg-primary text-white transition hover:bg-primary/90 disabled:opacity-40"
            >
              <Send size={13} />
            </button>
          </form>
        </div>
      </div>
    </>
  )
}
